import { Button, Select } from "@rio-cloud/rio-uikit";
import Card from "@rio-cloud/rio-uikit/Card";
import LoadMoreButton from "@rio-cloud/rio-uikit/LoadMoreButton";
import ButtonToolbar from "@rio-cloud/rio-uikit/ButtonToolbar";
import StepButton from "@rio-cloud/rio-uikit/StepButton";

interface OnboardingModalProps {
  show: boolean;
  step: number;
  numFiliais: number;
  filiaisNames: string[];
  isSaving?: boolean;
  onClose: () => void;
  onNext: () => void;
  onPrev: () => void;
  onNumFiliaisChange: (num: number) => void;
  onFilialNameChange: (index: number, value: string) => void;
}

export default function OnboardingModal({
  show,
  step,
  numFiliais,
  filiaisNames,
  isSaving = false,
  onClose,
  onNext,
  onPrev,
  onNumFiliaisChange,
  onFilialNameChange,
}: OnboardingModalProps) {
  if (!show) return null;

  const requiredFilialNames = Array.from({ length: numFiliais }, (_, index) => filiaisNames[index] ?? "");
  const areAllFiliaisFilled = requiredFilialNames.every((name) => name.trim() !== "");
  const isNextDisabled = isSaving || !areAllFiliaisFilled;

  return (
    <div className="position-fixed top-0 left-0 right-0 bottom-0 display-flex align-items-center justify-content-center padding-20 z-index-max">
      <div className="position-absolute top-0 left-0 right-0 bottom-0 bg-black opacity-50" />

      <Card className="bg-white width-50pct min-height-50vh position-relative display-flex flex-column z-index-1">
        <div className="padding-x-20 display-flex justify-content-end">
          <Button bsStyle={Button.MUTED} iconOnly iconName="rioglyph-remove" onClick={onClose} />
        </div>

        <div className="flex-1 overflow-visible padding-25 padding-x-40 padding-bottom-20">
          <div className="text-center">
            {step === 1 && (
              <>
                <span className="text-size-16 font-regular">Bem vindo ao</span>
                <br />
                <span className="text-size-h3 font-bold">Gerenciador Financeiro</span>
              </>
            )}
            {step === 2 && <span className="text-size-h3 font-bold">Quantas regionais você gerencia?</span>}
            {step === 3 && <span className="text-size-h3 font-bold">Tudo pronto!</span>}
          </div>

          {step === 1 && (
            <div className="display-flex align-items-center flex-column justify-content-start">
              <span className="rioglyph rioglyph-stats margin-y-20 text-color-primary" style={{ fontSize: "90px" }} />

              <p className="text-center text-size-16 font-regular">
                Aqui você acompanha de forma simples e visual os resultados da sua frota,
                <br />
                com DRE, gráficos e controle total de receitas e despesas.
              </p>
            </div>
          )}

          {step === 2 && (
            <div className="display-flex align-items-center flex-column justify-content-start">
              <span className="text-center margin-bottom-10">
                Essa informação é importante para organizarmos seus relatórios.
              </span>

              <div className="form-group width-50pct position-relative margin-top-10">
                <label>Regionais</label>
                <div>
                  <Select
                    value={[numFiliais.toString()]}
                    onChange={(option) => onNumFiliaisChange(Number(option?.id) || 1)}
                    options={[
                      { id: "1", label: "1" },
                      { id: "2", label: "2" },
                      { id: "3", label: "3" },
                      { id: "4", label: "4" },
                      { id: "5", label: "5" },
                    ]}
                    placeholder="Selecione"
                  />
                </div>
              </div>

              {numFiliais > 1 && (
                <div className="width-100pct display-flex align-items-center flex-column">
                  {Array.from({ length: numFiliais }, (_, i) => (
                    <div key={i} className="form-group width-50pct">
                      <label>Nome da regional {i + 1}</label>
                      <input
                        type="text"
                        className="form-control"
                      value={filiaisNames[i] ?? ""}
                        onChange={(e) => onFilialNameChange(i, e.target.value)}
                        placeholder={`Ex: ${i === 0 ? "São Paulo" : i === 1 ? "Rio de Janeiro" : "Florianópolis"}`}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="display-flex align-items-center flex-column justify-content-start text-center">
              <span className="rioglyph rioglyph-ok-sign margin-y-20 text-color-primary" style={{ fontSize: "90px" }} />

              <p className="text-size-16 font-regular">Comece a utilizar seu Gestor Financeiro agora mesmo.</p>
            </div>
          )}
        </div>

        <div className="padding-20 padding-x-40 margin-top-auto position-relative">
          <div className="position-absolute left-50pct translate-x-50pct bottom--20">
            <LoadMoreButton loaded={step} total={3} onLoadMore={() => {}} isInteractive={false} />
          </div>

          <ButtonToolbar alignment="space-between">
            {step > 1 && (
              <StepButton direction="previous" onClick={onPrev} className="width-20pct" >
                Voltar
              </StepButton>
            )}
            {step === 1 && <div />}

            <StepButton
              direction="next"
              bsStyle="primary"
              onClick={onNext}
              disabled={isNextDisabled}
              className="width-20pct"
            >
              {isSaving ? "Salvando..." : step === 3 ? "Concluir" : "Avançar"}
            </StepButton>
          </ButtonToolbar>
        </div>
      </Card>
    </div>
  );
}
